import { gql, useQuery } from '@apollo/client';
import IconCopy from '@apollo/icons/default/IconCopy.svg';
import IconDownload from '@apollo/icons/default/IconDownload.svg';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React from 'react';

import { GraphqlCodeBlock } from 'src/components/graphqlCodeBlock/GraphqlCodeBlock';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { Loading } from '../common/loading/Loading';
import { ErrorMessage } from '../errorMessage/ErrorMessage';

const getSchemaByHash = gql<
  GraphQLTypes.GetSchemaByHash,
  GraphQLTypes.GetSchemaByHashVariables
>`
  query GetSchemaByHash($graphId: ID!, $hash: SHA256!) {
    service(id: $graphId) {
      id
      document(hash: $hash)
    }
  }
`;

// exactly one of hash or sdl must be provided
interface Props {
  isOpen: boolean;
  graphId: string;
  onClose: () => void;
  sdl?: string | null;
  hash?: string | null;
  isCsdl?: boolean;
  loading?: boolean;
  subgraphName?: string;
}

const CopySDLButton = ({ sdl }: { sdl: string }) => {
  const [shouldPromptCopySuccess, setShouldPromptCopySuccess] =
    React.useState(false);
  React.useEffect(() => {
    if (shouldPromptCopySuccess) {
      const timeout = setTimeout(() => {
        setShouldPromptCopySuccess(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [shouldPromptCopySuccess]);

  return (
    <Button
      size="sm"
      variant="hidden"
      type="button"
      leftIcon={<IconCopy />}
      onClick={() => {
        copy(sdl);
        setShouldPromptCopySuccess(true);
      }}
      className={classnames(
        shouldPromptCopySuccess && 'cursor-default hover:bg-transparent',
      )}
    >
      {shouldPromptCopySuccess ? 'Copied' : 'Copy'}
    </Button>
  );
};

export const ViewSchemaModal = ({
  isOpen,
  sdl,
  hash,
  graphId,
  onClose,
  isCsdl,
  loading,
  subgraphName,
}: Props) => {
  const {
    data,
    loading: hashLoading,
    error: hashError,
  } = useQuery(getSchemaByHash, {
    variables: {
      graphId,
      hash: hash || '',
    },
    skip: !!(!hash || loading || sdl),
  });
  const fileName = subgraphName
    ? `${subgraphName}.graphql`
    : `${graphId}.graphql`;

  let computedError: boolean;
  let computedLoading: boolean;
  let computedSdl: string | null;

  if (loading || hashLoading) {
    computedError = false;
    computedLoading = true;
    computedSdl = null;
  } else if (hashError) {
    computedError = true;
    computedLoading = false;
    computedSdl = null;
  } else if (sdl) {
    computedError = false;
    computedLoading = false;
    computedSdl = sdl;
  } else if (hash && !!data?.service?.document) {
    computedError = false;
    computedLoading = false;
    computedSdl = data.service.document;
  } else {
    computedError = true;
    computedLoading = false;
    computedSdl = null;
  }

  return (
    <Modal isOpen={isOpen} size="6xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            View {isCsdl ? 'supergraph' : subgraphName} schema
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {computedLoading ? (
            <Loading />
          ) : computedError ? (
            <ErrorMessage>Could not load SDL.</ErrorMessage>
          ) : (
            <>
              <div className="flex justify-end gap-2">
                <Button
                  size="sm"
                  variant="hidden"
                  type="button"
                  leftIcon={<IconDownload />}
                  onClick={() => {
                    saveAs(
                      new Blob([computedSdl ?? ''], {
                        type: 'text/plain;charset=utf-8',
                      }),
                      fileName,
                    );
                  }}
                >
                  Download raw
                </Button>

                <CopySDLButton sdl={computedSdl ?? ''} />
              </div>
              <GraphqlCodeBlock className="text-sm" sdl={computedSdl ?? ''} />
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
